import { FocusQueueItem } from '@business/entities/focusMode';

/**
 * A stable identity for a queue entry. Deal 12 and task 12 are different items, so the
 * type has to be part of the key.
 */
export function focusItemKey(item: FocusQueueItem): string {
  return `${item.type}-${item.id}`;
}

export function focusItemTitle(item: FocusQueueItem): string {
  if (item.type === 'deal') return item.deal?.name ?? '';
  return item.task?.title ?? '';
}

/** The organization a queue item belongs to, via the item itself or its linked deal. */
export function focusItemOrgName(item: FocusQueueItem): string {
  if (item.type === 'deal') return item.deal?.orgName ?? '';
  return item.linkedDeal?.orgName ?? item.task?.organization ?? '';
}

/* -------------------------------------------------------------------- filters */

export type FocusQueueFilter = 'all' | 'closing' | 'neglected' | 'new_leads' | 'high_value' | 'tasks';

export const FOCUS_QUEUE_FILTERS: { key: FocusQueueFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'closing', label: 'Closing this week' },
  { key: 'neglected', label: 'Neglected' },
  { key: 'new_leads', label: 'New leads' },
  { key: 'high_value', label: 'High value' },
  { key: 'tasks', label: 'Tasks' },
];

/**
 * "High value" is the 75th percentile of the deal values actually in the queue rather
 * than a configured amount, so it stays meaningful whatever a tenant's deals look like.
 * Returns Infinity when nothing has a value, which makes the filter match nothing
 * instead of everything.
 */
export function focusHighValueThreshold(items: FocusQueueItem[]): number {
  const values = items
    .filter(i => i.type === 'deal' && !!i.deal && !i.deal.noValue && i.deal.value > 0)
    .map(i => i.deal!.value)
    .sort((a, b) => a - b);

  if (values.length === 0) return Infinity;

  return values[Math.floor(0.75 * (values.length - 1))];
}

export function focusItemMatchesFilter(item: FocusQueueItem, filter: FocusQueueFilter, warningDays: number, highValueThreshold: number): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'closing': {
      if (item.type !== 'deal' || !item.deal?.expectedClosed) return false;
      const days = Math.floor((item.deal.expectedClosed * 1000 - Date.now()) / 86400000);
      return days >= 0 && days <= 7;
    }
    case 'neglected':
      return item.daysSinceActivity >= warningDays;
    case 'new_leads':
      return item.isUncontacted;
    case 'high_value':
      return item.type === 'deal' && !!item.deal && !item.deal.noValue && item.deal.value >= highValueThreshold;
    case 'tasks':
      return item.type === 'task';
    default:
      return true;
  }
}

/* -------------------------------------------------------------------- urgency */

export type FocusUrgency = 'critical' | 'high' | 'medium' | 'low';

/**
 * Urgency is relative to the rest of the queue, not absolute: the top item is always
 * critical, and everything else is placed against it. `rank` is already normalised
 * across deals and tasks, so the comparison is fair between the two.
 */
export function focusItemUrgency(item: FocusQueueItem, maxRank: number): FocusUrgency {
  const ratio = maxRank > 0 ? item.rank / maxRank : 0;

  if (ratio >= 0.75) return 'critical';
  if (ratio >= 0.5) return 'high';
  if (ratio >= 0.25) return 'medium';
  return 'low';
}

export function focusUrgencyColor(level: FocusUrgency): string {
  switch (level) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'secondary';
  }
}

/* -------------------------------------------------- suggested next actions */

/**
 * What to do next, inferred from the stage the deal sits in.
 *
 * Keyword matching on the stage title rather than a configured mapping: stage names are
 * tenant-defined free text, so there is nothing to key a lookup off. Deliberately a
 * suggestion — it never blocks or replaces what the rep chooses to do.
 */
export function focusStageActions(stageTitle?: string | null): string[] {
  if (!stageTitle) return [];

  const name = stageTitle.toLowerCase();

  if (/new|lead|reach|initial|prospect/.test(name)) {
    return ['Send an intro email', 'Call to introduce yourself and qualify', 'Confirm their contact details'];
  }
  if (/contact|follow|discovery|qualif/.test(name)) {
    return ['Email a value proposition', 'Call to discuss their needs', 'Book a meeting'];
  }
  if (/proposal|quote|estimate/.test(name)) {
    return ['Send the proposal', 'Call to walk through pricing', 'Follow up on the review'];
  }
  if (/negotiat|review|decision/.test(name)) {
    return ['Call to address objections', 'Email revised terms', 'Check their decision timeline'];
  }
  if (/won|closed|handover|logistic/.test(name)) {
    return ['Send a welcome or onboarding email', 'Call to confirm next steps', 'Schedule the kickoff'];
  }

  return ['Email an update', 'Call for a check-in', 'Log where this stands'];
}
